import { ReactNode } from 'react';
import {
  Columns,
  Filter,
  SortAsc,
  Group,
  Eye,
  ZoomIn,
  ZoomOut,
  Calendar,
  Maximize2,
  Clock,
  AlertTriangle,
  GitBranch,
  BarChart3,
  Layers,
  ArrowUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { ViewModeSelector } from './ViewModeSelector';
import { cn } from '@/lib/utils';
import type { ViewMode } from '@/stores/useViewStore';
import type { GanttViewMode } from '@/lib/gantt/types';

interface ViewToolbarProps {
  currentMode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  children?: ReactNode;
  className?: string;
}

export function ViewToolbar({
  currentMode,
  onModeChange,
  children,
  className,
}: ViewToolbarProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b bg-background px-3 py-1.5',
        className
      )}
    >
      <ViewModeSelector currentMode={currentMode} onModeChange={onModeChange} />
      <Separator orientation="vertical" className="mx-1 h-6" />
      {children}
    </div>
  );
}

// Grid-specific toolbar items
interface GridToolbarProps {
  columns: { id: string; name: string; visible: boolean }[];
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  onFilter?: () => void;
  onSort?: () => void;
  onGroup?: () => void;
}

export function GridToolbarItems({
  columns,
  onColumnVisibilityChange,
  onFilter,
  onSort,
  onGroup,
}: GridToolbarProps) {
  return (
    <>
      {/* Column visibility */}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2">
                <Columns className="h-4 w-4" />
                <span className="text-xs">Columns</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>Show/Hide Columns</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>Visible Columns</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {columns.map((col) => (
            <DropdownMenuCheckboxItem
              key={col.id}
              checked={col.visible}
              onCheckedChange={(checked) =>
                onColumnVisibilityChange(col.id, checked)
              }
            >
              {col.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Filter */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={onFilter}>
            <Filter className="h-4 w-4" />
            <span className="text-xs">Filter</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Filter Tasks</TooltipContent>
      </Tooltip>

      {/* Sort */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={onSort}>
            <SortAsc className="h-4 w-4" />
            <span className="text-xs">Sort</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Sort Tasks</TooltipContent>
      </Tooltip>

      {/* Group */}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2">
                <Group className="h-4 w-4" />
                <span className="text-xs">Group</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>Group Tasks</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>Group By</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {columns.filter(c => ['Status', 'Assigned To', 'Priority'].includes(c.name)).map((col) => (
            <DropdownMenuCheckboxItem
              key={col.id}
              checked={false}
              onCheckedChange={() => onGroup?.()}
            >
              {col.name}
            </DropdownMenuCheckboxItem>
          ))}
          {columns.filter(c => ['Status', 'Assigned To', 'Priority'].includes(c.name)).length === 0 && (
            <div className="px-2 py-1.5 text-xs text-gray-500">No groupable columns</div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

// Gantt-specific toolbar items
interface GanttToolbarProps {
  viewMode: GanttViewMode;
  showDependencies: boolean;
  showProgress: boolean;
  showCriticalPath: boolean;
  showBaselines: boolean;
  onViewModeChange: (mode: GanttViewMode) => void;
  onTodayClick: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToScreen: () => void;
  onToggleDependencies: () => void;
  onToggleProgress: () => void;
  onToggleCriticalPath: () => void;
  onToggleBaselines: () => void;
}

const ganttViewModes: { value: GanttViewMode; label: string }[] = [
  { value: 'Day', label: 'Day' },
  { value: 'Week', label: 'Week' },
  { value: 'Month', label: 'Month' },
  { value: 'Year', label: 'Year' },
];

export function GanttToolbarItems({
  viewMode,
  showDependencies,
  showProgress,
  showCriticalPath,
  showBaselines,
  onViewModeChange,
  onTodayClick,
  onZoomIn,
  onZoomOut,
  onFitToScreen,
  onToggleDependencies,
  onToggleProgress,
  onToggleCriticalPath,
  onToggleBaselines,
}: GanttToolbarProps) {
  return (
    <>
      {/* View Mode Buttons */}
      <div className="flex items-center rounded-md border bg-muted p-0.5">
        {ganttViewModes.map((mode) => (
          <Button
            key={mode.value}
            variant="ghost"
            size="sm"
            className={cn(
              'h-6 px-2 text-xs',
              viewMode === mode.value && 'bg-background shadow-sm'
            )}
            onClick={() => onViewModeChange(mode.value)}
          >
            {mode.label}
          </Button>
        ))}
      </div>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Navigation */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onTodayClick}>
            <Calendar className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Go to Today</TooltipContent>
      </Tooltip>

      {/* Zoom Controls */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onZoomOut}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Zoom Out</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onZoomIn}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Zoom In</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onFitToScreen}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Fit to Screen</TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Display Options */}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <Eye className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>Display Options</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuCheckboxItem
            checked={showDependencies}
            onCheckedChange={onToggleDependencies}
          >
            <GitBranch className="mr-2 h-4 w-4" />
            Dependencies
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={showProgress}
            onCheckedChange={onToggleProgress}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Progress Bars
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={showCriticalPath}
            onCheckedChange={onToggleCriticalPath}
          >
            <AlertTriangle className="mr-2 h-4 w-4" />
            Critical Path
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={showBaselines}
            onCheckedChange={onToggleBaselines}
          >
            <Clock className="mr-2 h-4 w-4" />
            Baselines
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Quick toggles */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={showCriticalPath ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={onToggleCriticalPath}
          >
            <AlertTriangle
              className={cn('h-4 w-4', showCriticalPath && 'text-destructive')}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {showCriticalPath ? 'Hide' : 'Show'} Critical Path
        </TooltipContent>
      </Tooltip>
    </>
  );
}

// Card view toolbar items
interface CardToolbarProps {
  groupByColumn: string | null;
  availableColumns: { id: string; name: string }[];
  wipLimits: Record<string, number>;
  onGroupByChange: (columnId: string | null) => void;
  onWipLimitChange: (columnValue: string, limit: number) => void;
}

export function CardToolbarItems({
  groupByColumn,
  availableColumns,
  onGroupByChange,
}: CardToolbarProps) {
  return (
    <>
      {/* Group by column */}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2">
                <Layers className="h-4 w-4" />
                <span className="text-xs">
                  {groupByColumn
                    ? availableColumns.find((c) => c.id === groupByColumn)?.name ||
                      'Group By'
                    : 'Group By'}
                </span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>Group Cards By Column</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>Group By</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={groupByColumn === null}
            onCheckedChange={() => onGroupByChange(null)}
          >
            None
          </DropdownMenuCheckboxItem>
          {availableColumns.map((col) => (
            <DropdownMenuCheckboxItem
              key={col.id}
              checked={groupByColumn === col.id}
              onCheckedChange={() => onGroupByChange(col.id)}
            >
              {col.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Sort order */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2">
            <ArrowUpDown className="h-4 w-4" />
            <span className="text-xs">Sort</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Sort Cards</TooltipContent>
      </Tooltip>
    </>
  );
}

// Calendar view toolbar items
interface CalendarToolbarProps {
  calendarView: 'month' | 'week';
  onViewChange: (view: 'month' | 'week') => void;
  onTodayClick: () => void;
  onPrevious: () => void;
  onNext: () => void;
  currentDate: Date;
}

export function CalendarToolbarItems({
  calendarView,
  onViewChange,
  onTodayClick,
}: CalendarToolbarProps) {
  return (
    <>
      {/* View Mode Toggle */}
      <div className="flex items-center rounded-md border bg-muted p-0.5">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-6 px-2 text-xs',
            calendarView === 'month' && 'bg-background shadow-sm'
          )}
          onClick={() => onViewChange('month')}
        >
          Month
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-6 px-2 text-xs',
            calendarView === 'week' && 'bg-background shadow-sm'
          )}
          onClick={() => onViewChange('week')}
        >
          Week
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-1 h-6" />

      {/* Today button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onTodayClick}>
            Today
          </Button>
        </TooltipTrigger>
        <TooltipContent>Go to Today</TooltipContent>
      </Tooltip>
    </>
  );
}

export default ViewToolbar;
